/**
 * Фольга: перелив, следующий за наклоном телефона или за пальцем.
 *
 * Два источника, потому что ни один не работает везде:
 *
 *   наклон — есть только внутри Telegram и только с Bot API 8.0. Голый
 *            DeviceOrientationEvent в WKWebView отдаёт 'denied' без промпта,
 *            то есть на iOS не работает вовсе;
 *   палец  — работает всегда, но только пока по карте водят.
 *
 * Наклон приходит СНАРУЖИ, пропом. Пакет карт не имеет права знать про
 * Telegram — это правило границ (docs/plan/11-modules.md), и оно здесь
 * оказалось полезным по существу: карта одинаково работает и в мини-аппе,
 * и в витрине пакета, и в любом другом проекте, куда её заберут.
 *
 * Переменные пишутся напрямую в стиль элемента, минуя состояние React.
 * Через useState это был бы полный проход рендера на каждое движение
 * пальца — 60 раз в секунду на дерево компонентов.
 */

import { useCallback, useEffect, useRef } from 'react'

/**
 * Отклонение −1…1 по каждой оси.
 *
 * Тип объявлен здесь, а не импортирован из @taro/tg, ровно по той же
 * причине: структурно он совпадает с Tilt, и приложение передаёт одно
 * в другое без единой строки преобразования.
 */
export interface CardTilt {
  x: number
  y: number
}

/** Насколько сильно наклон превращается в перелив. Подобрано на глаз. */
const TILT_GAIN = 1.35

function write(el: HTMLElement, x: number, y: number, strength: number): void {
  // Угол конического градиента = направление наклона. Плюс 90°, чтобы
  // при наклоне вправо блик шёл вправо, а не вверх.
  const angle = (Math.atan2(y, x) * 180) / Math.PI + 90

  el.style.setProperty('--holo-angle', `${angle.toFixed(1)}deg`)
  el.style.setProperty('--holo-strength', strength.toFixed(3))
  el.style.setProperty('--holo-x', `${(50 + x * 42).toFixed(1)}%`)
  el.style.setProperty('--holo-y', `${(50 + y * 42).toFixed(1)}%`)
}

function clear(el: HTMLElement): void {
  el.style.setProperty('--holo-strength', '0')
}

export function useHolo(enabled: boolean, tilt?: CardTilt | null) {
  // Ref именно колбэком: корень карты — это либо <div>, либо <button>,
  // и объектный ref одного типа не подошёл бы обоим без приведения.
  const ref = useRef<HTMLElement | null>(null)
  const setRef = useCallback((el: HTMLElement | null) => {
    ref.current = el
  }, [])
  // Пока по карте водят пальцем, наклон игнорируется: иначе блик тянут
  // в две стороны сразу и он дрожит.
  const touching = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (!enabled || !tilt || touching.current) {
      if (!enabled) clear(el)
      return
    }

    const strength = Math.min(1, Math.hypot(tilt.x, tilt.y) * TILT_GAIN)
    write(el, tilt.x, tilt.y, strength)
  }, [enabled, tilt])

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const el = ref.current
      if (!enabled || !el) return

      touching.current = true
      const r = el.getBoundingClientRect()
      // Палец даёт позицию, а не наклон, поэтому сила максимальная:
      // человек трогает карту намеренно.
      write(el, ((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1, 1)
    },
    [enabled],
  )

  const onPointerLeave = useCallback(() => {
    touching.current = false
    const el = ref.current
    if (el) clear(el)
  }, [])

  return { ref: setRef, holoProps: enabled ? { onPointerMove, onPointerLeave } : {} }
}
